<script setup lang="ts">
import { Html5Qrcode, type Html5QrcodeCameraScanConfig, type Html5QrcodeResult, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useTranslation } from "i18next-vue";
import { motion } from "motion-v";
import { onBeforeUnmount, onMounted } from "vue";
import { emitter } from "#/utils/emitter";

interface Props {
	config?: Html5QrcodeCameraScanConfig;
	formats?: Html5QrcodeSupportedFormats[];
}

interface Emits {
	"scan-success": [decodedText: string, decodedResult: Html5QrcodeResult];
	"scan-error": [error: string];
	"close-scanner": [];
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const BASIC_CONFIG = { fps: 24, qrbox: { width: 320, height: 500 } };
const BASIC_FORMATS = [
	Html5QrcodeSupportedFormats.QR_CODE,
	Html5QrcodeSupportedFormats.PDF_417,
	Html5QrcodeSupportedFormats.EAN_13,
	Html5QrcodeSupportedFormats.EAN_8,
	Html5QrcodeSupportedFormats.CODE_128,
];

const { t } = useTranslation();

let html5QrCodeInstance: Html5Qrcode | null = null;

const onScanSuccess = (decodedText: string, decodedResult: Html5QrcodeResult) => {
	emit("scan-success", decodedText, decodedResult);
};

const onScanFailure = (error: string) => {
	emit("scan-error", error);
};

const closeScanner = () => {
	emit("close-scanner");
};

onMounted(() => {
	const config = { ...BASIC_CONFIG, ...props.config };
	const formats = props.formats ? props.formats : BASIC_FORMATS;

	html5QrCodeInstance = new Html5Qrcode(`camera-reader`, { formatsToSupport: formats, verbose: false });
	html5QrCodeInstance.start({ facingMode: "user" }, config, onScanSuccess, onScanFailure);
	emitter.emit("keyboard:close");
});

onBeforeUnmount(() => {
	if (html5QrCodeInstance) {
		html5QrCodeInstance.stop().catch((error) => {
			html5QrCodeInstance?.clear();

			console.error("Ошибка остановки сканера:", error);
		});
		html5QrCodeInstance = null;
	}
});
</script>

<template>
	<Teleport to="body">
		<div class="scanner-popup">
			<div id="camera-reader" class="reader"></div>

			<div class="scanner-footer">
				<p class="scanner-title">{{ t(`camera-scanner.title`) }}</p>
				<motion.button type="button" class="scanner-close-button" @click="closeScanner"> {{ t(`camera-scanner.close`) }} </motion.button>
			</div>
		</div>
	</Teleport>
</template>

<style lang="scss" scoped>
@use './scanner.scss';
</style>
